import { mustParse } from './geometry.ts';
import { applyAction, applyActionOrThrow } from './apply.ts';
import type { Block } from './reasons.ts';
import { createGameWithEvents, type CreateGameOptions } from './create.ts';
import type { GameSizeId } from './sizes.ts';
import type { Action, CellId, GameEvent, GameState, MapDef, Player, Ruleset, Side } from './types.ts';
import {
  activePortFort,
  afloat,
  findShip,
  portReachable,
  shooterBlock,
  targetsOf,
  windReachable,
} from './world.ts';

/** Cells the ship can reach with its wind/calm move under the current spin. */
export function reachableCells(state: GameState, shipId: string): CellId[] {
  const ship = findShip(state, shipId);
  return ship ? windReachable(state, ship) : [];
}

/** Cells of its own port the ship can reach with an in-port move (R-PORT-03). */
export function portCellsReachable(state: GameState, shipId: string): CellId[] {
  const ship = findShip(state, shipId);
  return ship ? portReachable(state, ship) : [];
}

/** Enemy units within range and line of fire of the shooter (ship or fort). */
export function targetsInRange(state: GameState, shooterId: string): string[] {
  return targetsOf(state, shooterId);
}

/**
 * Enemy units the active side's `shooterId` may fire at now: those in range and line of fire,
 * or none when the shooter itself may not fire (no shots left, in dock, on fort repair, on the
 * enemy Royal Port — R-FIRE-04/06, R-ROYAL-03). What "can still fire" means to a client.
 */
export function fireTargets(state: GameState, shooterId: string): string[] {
  return shooterBlock(state, state.turn.side, shooterId, 1) === null ? targetsOf(state, shooterId) : [];
}

/**
 * Why `action` is illegal now (rule and reason), or null if it is legal. A dry run: the state
 * is not changed. Clients use it to explain disabled actions.
 */
export function explain(state: GameState, action: Action): Block | null {
  const result = applyAction(state, action);
  return result.ok ? null : { rule: result.error.rule, reason: result.error.reason };
}

/** Ships of the active side that may join a movement group now (R-TURN-03). */
export function declarableShips(state: GameState): string[] {
  if (state.turn.phase !== 'open') return [];
  return afloat(state, state.turn.side)
    .filter((s) => s.dockedOnTurn === null && s.repairingFort === null && !s.moved && !s.firedInPort)
    .map((s) => s.id);
}

/** Own shooters (ships and forts) able to fire at least one shot at `targetId` now. */
export function volleyCandidates(state: GameState, targetId: string): string[] {
  const side = state.turn.side;
  const ids = [...state.ships.map((s) => s.id), ...state.forts.map((f) => f.id)];
  return ids.filter(
    (id) => shooterBlock(state, side, id, 1) === null && targetsOf(state, id).includes(targetId),
  );
}

/**
 * Legal actions for `side`. Exhaustive for single-unit actions. Combinatorial actions are
 * represented by canonical members: a group of all declarable ships plus every single-ship
 * group; one volley per target with every candidate committing all its shots; one fort-repair
 * crew per fort (the first eligible ships). Use the helpers above to build other variants.
 */
export function legalActions(state: GameState, side: Side): Action[] {
  if (state.status.kind !== 'playing') return [];
  const actions: Action[] = [
    { type: 'resign', side },
    { type: 'offerDraw', side },
  ];
  if (state.drawOffer && state.drawOffer.by !== side) actions.push({ type: 'acceptDraw', side });
  if (side !== state.turn.side) return actions;

  const { phase } = state.turn;
  if (phase === 'open') {
    const ids = declarableShips(state);
    if (ids.length > 0) {
      actions.push({ type: 'declareMoveGroup', side, shipIds: ids });
      if (ids.length > 1) {
        for (const id of ids) actions.push({ type: 'declareMoveGroup', side, shipIds: [id] });
      }
    }
  }
  if (phase === 'declared') {
    actions.push({ type: 'spinForMovement', side });
  } else {
    actions.push({ type: 'endTurn', side });
  }

  for (const ship of afloat(state, side)) {
    for (const to of windReachable(state, ship)) {
      actions.push({ type: 'moveShip', side, shipId: ship.id, to });
    }
    for (const to of portReachable(state, ship)) {
      actions.push({ type: 'moveInPort', side, shipId: ship.id, to });
    }
    const fort = activePortFort(state, mustParse(ship.cell));
    if (
      fort?.side === side &&
      ship.damage > 0 &&
      ship.dockedOnTurn === null &&
      ship.repairingFort === null &&
      !ship.fired
    ) {
      actions.push({ type: 'enterPortRepair', side, shipId: ship.id });
    }
  }

  const shooters = [...state.ships.map((s) => s.id), ...state.forts.map((f) => f.id)].filter(
    (id) => shooterBlock(state, side, id, 1) === null,
  );
  const targets = new Map<string, string[]>();
  for (const shooterId of shooters) {
    for (const targetId of targetsOf(state, shooterId)) {
      actions.push({ type: 'fire', side, shooterId, targetId });
      targets.set(targetId, [...(targets.get(targetId) ?? []), shooterId]);
    }
  }
  for (const [targetId, ids] of targets) {
    if (ids.length >= state.ruleset.volleyMinShooters) {
      actions.push({
        type: 'volley',
        side,
        targetId,
        shooters: ids.map((id) => {
          const ship = state.ships.find((s) => s.id === id);
          const shots = ship ? ship.shotsLeft : (state.forts.find((f) => f.id === id)?.shotsLeft ?? 0);
          return { id, shots };
        }),
      });
    }
  }

  for (const fort of state.forts) {
    if (fort.repair) {
      if (fort.repair.side === side) actions.push({ type: 'stopFortRepair', side, fortId: fort.id });
      continue;
    }
    const inPort = afloat(state).filter((s) => fort.portCells.includes(s.cell));
    if (fort.destroyed) {
      if (inPort.some((s) => s.side !== side)) continue;
    } else if (fort.side !== side || fort.hits === 0) {
      continue;
    }
    const crew = inPort
      .filter((s) => s.side === side && s.dockedOnTurn === null && s.repairingFort === null && !s.fired)
      .slice(0, state.ruleset.fortRepairCrew)
      .map((s) => s.id);
    if (crew.length === state.ruleset.fortRepairCrew) {
      actions.push({ type: 'startFortRepair', side, fortId: fort.id, crew });
    }
  }
  return actions;
}

/** Everything needed to recreate a game from scratch. */
export interface GameSetup {
  /** Game size (sizes.ts); absent in games from before sizes. Informational: map and ruleset rule. */
  readonly size?: GameSizeId;
  readonly map: MapDef;
  readonly ruleset: Partial<Ruleset>;
  readonly seed: number;
  readonly players: Readonly<Record<Side, Player>>;
  readonly options?: CreateGameOptions;
}

/**
 * Deterministically rebuilds a game from its setup and action log (R-RNG-02).
 * Throws `RuleViolation` if an action is illegal.
 */
export function replay(
  setup: GameSetup,
  actions: readonly Action[],
): { state: GameState; events: GameEvent[] } {
  const created = createGameWithEvents(setup.map, setup.ruleset, setup.seed, setup.players, setup.options);
  let state = created.state;
  const events = [...created.events];
  for (const action of actions) {
    const result = applyActionOrThrow(state, action);
    state = result.state;
    events.push(...result.events);
  }
  return { state, events };
}
